/**
 * 地形/物件筆刷編輯器
 *
 * 在 Three.js 場景中用 Raycaster 追蹤滑鼠位置，
 * 轉換為高度圖 UV 傳送給離屏侵蝕引擎。
 *
 * terrain → GPU 筆刷（ErosionEngine）
 * object → CPU 側散佈放置物件（brush area scatter）
 *
 * 包含 OrbitControls（右鍵旋轉、滾輪縮放）。
 * 左鍵拖曳繪製地形/物件，Shift 反轉（擦除）。
 */

import { useRef, useEffect, useCallback, useMemo } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import { OrbitControls } from '@react-three/drei'
import * as THREE from 'three/webgpu'
import { useGameStore } from '../core/store/gameStore'
import {
  TERRAIN_WORLD_SIZE,
  TERRAIN_HEIGHT_SCALE,
  DEFAULT_HEIGHT_RATIO,
  WATER_LEVEL,
  worldToUV,
} from '../core/terrainConstants'
import { terrainBuffer } from '../core/terrainBuffer'
import type { ErosionEngine } from '../core/ErosionEngine'

/** 地形工具 → shader uBrushTool 值對照 */
const TOOL_MAP: Record<string, number> = {
  raise: 0,
  dig: 1,
  flatten: 2,
  smooth: 3,
  roughen: 4,
}

interface TerrainEditorProps {
  engine: ErosionEngine
}

export default function TerrainEditor({ engine }: TerrainEditorProps) {
  const { camera, gl } = useThree()
  const controlsRef = useRef<any>(null)
  const previewRef = useRef<THREE.Mesh>(null)

  const toolGroup = useGameStore(s => s.toolGroup)
  const terrainTool = useGameStore(s => s.terrainTool)
  const brushRadius = useGameStore(s => s.brushRadius)
  const brushStrength = useGameStore(s => s.brushStrength)
  const selectedObjectType = useGameStore(s => s.selectedObjectType)
  const addObject = useGameStore(s => s.addObject)
  const removeObject = useGameStore(s => s.removeObject)
  const placedObjects = useGameStore(s => s.placedObjects)

  const isObjectMode = toolGroup === 'object'

  const raycaster = useMemo(() => new THREE.Raycaster(), [])
  const pointer = useRef(new THREE.Vector2())
  const hitPoint = useRef(new THREE.Vector3())
  const hasHit = useRef(false)
  const isPainting = useRef(false)
  const shiftDown = useRef(false)

  // 物件散佈節流（避免每幀都放物件）
  const lastScatterTime = useRef(0)

  // scatterObjects / eraseObjects 定義在後面，用 ref 在 onPointerDown 中參照
  const scatterRef = useRef<(center: THREE.Vector3) => void>(() => {})
  const eraseRef = useRef<(center: THREE.Vector3) => void>(() => {})

  // 記錄 pointerdown 位置（區分點擊與拖曳）
  const pointerDownPos = useRef<{ x: number; y: number } | null>(null)
  // 上次命中的地形高度（用作下次射線求交的起始猜測，加速收斂）
  const lastHitHeight = useRef(DEFAULT_HEIGHT_RATIO * TERRAIN_HEIGHT_SCALE)

  // 初始化俯瞰相機
  useEffect(() => {
    camera.position.set(0, 250, 120)
    camera.lookAt(0, 0, 0)
  }, [camera])

  // 追蹤游標
  const onPointerMove = useCallback((e: PointerEvent) => {
    const rect = gl.domElement.getBoundingClientRect()
    pointer.current.x = ((e.clientX - rect.left) / rect.width) * 2 - 1
    pointer.current.y = -((e.clientY - rect.top) / rect.height) * 2 + 1
  }, [gl.domElement])

  /**
   * 射線-地形求交：迭代平面法，從上次命中高度起算加速收斂。
   * 回傳地形表面點（Y = 真實地形高度），失敗回傳 false。
   */
  const rayTerrainHit = useCallback((ray: THREE.Ray, out: THREE.Vector3): boolean => {
    let planeY = lastHitHeight.current
    const tmp = new THREE.Vector3()
    for (let i = 0; i < 8; i++) {
      const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -planeY)
      if (!ray.intersectPlane(plane, tmp)) return false
      if (!terrainBuffer.ready) break
      const h = terrainBuffer.getHeight(tmp.x, tmp.z)
      // 收斂判定：高度差 < 0.5 世界單位即停止
      if (Math.abs(h - planeY) < 0.5) {
        out.set(tmp.x, h, tmp.z)
        lastHitHeight.current = h
        return true
      }
      planeY = h
    }
    // 即使未完全收斂，也使用最後一輪的地形高度
    if (terrainBuffer.ready) {
      const h = terrainBuffer.getHeight(tmp.x, tmp.z)
      out.set(tmp.x, h, tmp.z)
      lastHitHeight.current = h
      return true
    }
    out.copy(tmp)
    return true
  }, [])

  // 即時射線求交 — 不依賴 useFrame 的 hasHit 快取
  const raycastImmediate = useCallback((e: PointerEvent): boolean => {
    const rect = gl.domElement.getBoundingClientRect()
    const px = ((e.clientX - rect.left) / rect.width) * 2 - 1
    const py = -((e.clientY - rect.top) / rect.height) * 2 + 1
    raycaster.setFromCamera(new THREE.Vector2(px, py), camera)
    if (!rayTerrainHit(raycaster.ray, hitPoint.current)) return false
    hasHit.current = true
    return true
  }, [gl.domElement, camera, raycaster, rayTerrainHit])

  // 開始繪製
  const onPointerDown = useCallback((e: PointerEvent) => {
    if (e.button !== 0) return
    pointerDownPos.current = { x: e.clientX, y: e.clientY }
    isPainting.current = true

    if (isObjectMode) {
      // 即時射線求交保證最新位置
      const worldRadius = brushRadius * TERRAIN_WORLD_SIZE
      if (worldRadius < 8 && raycastImmediate(e)) {
        if (shiftDown.current) {
          eraseRef.current(hitPoint.current)
        } else {
          scatterRef.current(hitPoint.current)
        }
        lastScatterTime.current = performance.now()
      }
      return
    }

    // 非物件模式也做即時射線更新
    raycastImmediate(e)

    // 讀取 flatten 工具所需的目標高度
    if (toolGroup === 'terrain' && terrainTool === 'flatten' && hasHit.current && terrainBuffer.ready) {
      const hp = hitPoint.current
      const h = terrainBuffer.getHeight(hp.x, hp.z)
      let normH = h / TERRAIN_HEIGHT_SCALE
      // 近水面時降低目標，創造自然淺灘而非高台
      const waterNorm = WATER_LEVEL / TERRAIN_HEIGHT_SCALE
      if (normH < waterNorm + 0.08) {
        normH = waterNorm + 0.015
      }
      engine.setBrush({ target: normH })
    }

    // 更新筆刷狀態
    if (hasHit.current) {
      const [u, v] = worldToUV(hitPoint.current.x, hitPoint.current.z)
      engine.setBrush({
        active: true,
        position: [u, 1 - v],
        tool: TOOL_MAP[terrainTool] ?? 0,
        radius: brushRadius,
        strength: brushStrength,
        shift: shiftDown.current,
      })
    }
  }, [engine, toolGroup, terrainTool, brushRadius, brushStrength, isObjectMode, raycastImmediate, rayTerrainHit])

  // 結束繪製
  const onPointerUp = useCallback((e: PointerEvent) => {
    if (e.button !== 0) return
    isPainting.current = false
    pointerDownPos.current = null
    engine.setBrush({ active: false })
  }, [engine])

  // Shift 按鍵
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Shift') shiftDown.current = true
    }
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Shift') shiftDown.current = false
    }
    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('keyup', onKeyUp)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('keyup', onKeyUp)
    }
  }, [])

  // 繫結指標事件
  useEffect(() => {
    const el = gl.domElement
    el.addEventListener('pointermove', onPointerMove)
    el.addEventListener('pointerdown', onPointerDown)
    window.addEventListener('pointerup', onPointerUp)
    return () => {
      el.removeEventListener('pointermove', onPointerMove)
      el.removeEventListener('pointerdown', onPointerDown)
      window.removeEventListener('pointerup', onPointerUp)
    }
  }, [gl.domElement, onPointerMove, onPointerDown, onPointerUp])

  // 確保卸載時關閉筆刷
  useEffect(() => {
    return () => { engine.setBrush({ active: false }) }
  }, [engine])

  /** 在筆刷半徑內散佈放置物件（極小筆刷 = 單點模式） */
  const scatterObjects = useCallback((center: THREE.Vector3) => {
    const worldRadius = brushRadius * TERRAIN_WORLD_SIZE
    const type = useGameStore.getState().selectedObjectType
    const existing = useGameStore.getState().placedObjects
    const isTree = type.startsWith('tree_')
    const isFog = type === 'cloud_fog'
    // 單點模式：筆刷極小時只放一個
    const isSinglePoint = worldRadius < 8
    // 雲霧間距較大（體積大），單點放置間距寬鬆
    const minSpacing = isSinglePoint
      ? (isFog ? 15 : 3)
      : (isTree ? 12 : (type === 'rock' ? 8 : (isFog ? 20 : 5)))
    // 嘗試數量隨筆刷面積與力度同時縮放
    const radiusFactor = Math.max(1, worldRadius / 10)
    const attempts = isSinglePoint ? 1 : Math.ceil(brushStrength * radiusFactor * (isTree ? 4 : 10))
    const newPositions: Array<{ x: number; z: number }> = []

    for (let i = 0; i < attempts; i++) {
      let x: number, z: number
      if (isSinglePoint) {
        x = center.x
        z = center.z
      } else {
        const angle = Math.random() * Math.PI * 2
        const dist = Math.sqrt(Math.random()) * worldRadius
        x = center.x + Math.cos(angle) * dist
        z = center.z + Math.sin(angle) * dist
      }
      if (!terrainBuffer.ready) continue
      const y = terrainBuffer.getHeight(x, z)
      if (y < WATER_LEVEL + 1) continue
      // 坡度檢查：單點模式下用戶主動選位，完全跳過坡度限制
      if (!isSinglePoint) {
        const step = 3.0
        const hL = terrainBuffer.getHeight(x - step, z)
        const hR = terrainBuffer.getHeight(x + step, z)
        const hD = terrainBuffer.getHeight(x, z - step)
        const hU = terrainBuffer.getHeight(x, z + step)
        const slopeX = Math.abs(hR - hL) / (2 * step)
        const slopeZ = Math.abs(hU - hD) / (2 * step)
        const slope = Math.sqrt(slopeX * slopeX + slopeZ * slopeZ)
        const maxSlope = isTree ? 1.0 : 1.8
        if (slope > maxSlope) continue
      }
      // 最小間距：兼查已有物件與本輪新放置
      const tooClose = existing.some(obj => {
        const dx = obj.position[0] - x
        const dz = obj.position[2] - z
        return dx * dx + dz * dz < minSpacing * minSpacing
      }) || newPositions.some(p => {
        const dx = p.x - x
        const dz = p.z - z
        return dx * dx + dz * dz < minSpacing * minSpacing
      })
      if (tooClose) continue
      newPositions.push({ x, z })
      const id = `obj_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
      addObject({
        id,
        type,
        position: [x, y, z],
        rotation: Math.random() * Math.PI * 2,
        scale: 0.7 + Math.random() * 0.6,
      })
    }
  }, [brushRadius, brushStrength, addObject])

  /** Shift+筆刷：擦除半徑內物件 */
  const eraseObjects = useCallback((center: THREE.Vector3) => {
    const worldRadius = brushRadius * TERRAIN_WORLD_SIZE
    const existing = useGameStore.getState().placedObjects
    const toRemove: string[] = []
    for (const obj of existing) {
      const dx = obj.position[0] - center.x
      const dz = obj.position[2] - center.z
      if (dx * dx + dz * dz < worldRadius * worldRadius) {
        toRemove.push(obj.id)
      }
    }
    for (const id of toRemove) removeObject(id)
  }, [brushRadius, removeObject])

  // 保持 ref 同步（供 onPointerDown 單點模式使用）
  scatterRef.current = scatterObjects
  eraseRef.current = eraseObjects

  // 每幀更新 Raycaster + 筆刷
  useFrame(() => {
    raycaster.setFromCamera(pointer.current, camera)
    const target = new THREE.Vector3()

    // 使用統一的射線-地形求交
    const hit = rayTerrainHit(raycaster.ray, target)

    if (hit) {
      hasHit.current = true
      hitPoint.current.copy(target)

      // 更新預覽圈位置 — target.y 已是真實地形高度
      if (previewRef.current) {
        previewRef.current.position.set(target.x, target.y + 0.3, target.z)
        const worldRadius = brushRadius * TERRAIN_WORLD_SIZE
        // 單點模式：小型定位標記
        previewRef.current.scale.setScalar(worldRadius < 8 ? 2 : worldRadius)
        previewRef.current.visible = true
      }

      // 繪製中持續更新位置
      if (isPainting.current) {
        if (isObjectMode) {
          // 物件散佈模式（節流：每 120ms 一次）
          const now = performance.now()
          if (now - lastScatterTime.current > 120) {
            lastScatterTime.current = now
            if (shiftDown.current) {
              eraseObjects(target)
            } else {
              scatterObjects(target)
            }
          }
        } else {
          // GPU 筆刷（地形）
          const [u, v] = worldToUV(target.x, target.z)
          engine.setBrush({
            active: true,
            position: [u, 1 - v],
            shift: shiftDown.current,
            radius: brushRadius,
            strength: brushStrength,
          })
        }
      }
    } else {
      hasHit.current = false
      if (previewRef.current) previewRef.current.visible = false
    }
  })

  return (
    <>
      {/* OrbitControls：右鍵旋轉、滾輪縮放、中鍵平移 */}
      <OrbitControls
        ref={controlsRef}
        makeDefault
        mouseButtons={{
          LEFT: -1 as any,
          MIDDLE: THREE.MOUSE.PAN,
          RIGHT: THREE.MOUSE.ROTATE,
        }}
        enableDamping
        dampingFactor={0.12}
        minDistance={20}
        maxDistance={700}
        zoomSpeed={0.25}
        rotateSpeed={0.6}
        panSpeed={0.8}
        enablePan
        screenSpacePanning
        minPolarAngle={0.2}
        maxPolarAngle={Math.PI / 2.8}
        target={[0, 0, 0]}
      />

      {/* 筆刷預覽圈 */}
      <mesh
        ref={previewRef}
        rotation={[-Math.PI / 2, 0, 0]}
        visible={false}
      >
        <ringGeometry args={[0.95, 1.0, 48]} />
        <meshBasicMaterial
          color={isObjectMode ? '#a8c0d8' : '#e0d4c0'}
          transparent
          opacity={0.6}
          depthTest={false}
        />
      </mesh>
    </>
  )
}
